// @/globals/SharedComponents.tsx
import styled, { css, keyframes } from "styled-components";
import type { DefaultTheme } from "styled-components";
import { alpha } from "./theme";
import { useCallback, useState } from "react";
import { ArrowSquareOutIcon, CheckIcon, CopyIcon } from "@phosphor-icons/react";
import { toast } from "sonner";
import { openURLNewTab } from "@/utils/helpers/url-navigation";

// ─── Animations ─────────────────────────────────────────────────────────────
// Canonical set. Distance/duration variance across the old files (4px/6px/8px,
// 180ms/220ms) was never intentional — callers control duration via
// `animation: ${fadeUp} <ms> ease`, the keyframe itself just defines direction.

export const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(25px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const expandDown = keyframes`
  from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); }
`;

export const fadeDown = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50%      { opacity: 0.5; }
`;

export const shimmer = keyframes`
  from { transform: translateX(-100%); }
  to   { transform: translateX(100%); }
`;

export const spin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const SpinWrap = styled.span`
  display: inline-flex;
  animation: ${spin} 800ms linear infinite;
`;

export const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to   { opacity: 1; transform: translateY(0); }
`;

export const shakeX = keyframes`
  0%, 100% { transform: translateX(0); }
  20%       { transform: translateX(-4px); }
  40%       { transform: translateX(4px); }
  60%       { transform: translateX(-3px); }
  80%       { transform: translateX(3px); }
`;

export const dropdownOpen = keyframes`
  from { opacity: 0; transform: translateY(-6px) scaleY(0.95); }
  to   { opacity: 1; transform: translateY(0) scaleY(1); }
`;

export const checkPop = keyframes`
  0%   { transform: scale(0.5) rotate(-10deg); opacity: 0; }
  60%  { transform: scale(1.15) rotate(2deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

// ─── Status tone resolver ─────────────────────────────────────────────────────
// Single source of truth mapping semantic intent → theme color.
// Nothing downstream should write a hex/rgba literal for status color again.

export type StatusTone =
  | "success"
  | "danger"
  | "warning"
  | "info"
  | "neutral"
  | "brand";

export function toneColor(tone: StatusTone, theme: DefaultTheme): string {
  switch (tone) {
    case "success":
      return theme.colors.success;
    case "danger":
      return theme.colors.error;
    case "warning":
      return theme.colors.warning;
    case "info":
      return theme.colors.info;
    case "brand":
      return theme.colors.primary;
    default:
      return theme.colors.textSecondary;
  }
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────
// Replaces CardStatusBadge (SignPanel) + EnvelopeTypeBadge's sibling logic.
// $pulse decouples the "in progress" animation from tone, so any tone can pulse.

export const StatusBadge = styled.span<{ $tone: StatusTone; $pulse?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border-radius: 100px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border: 1px solid transparent;
  white-space: nowrap;
  ${({ $tone, $pulse, theme }) => {
    const c = toneColor($tone, theme);
    return css`
      background: ${alpha(c, $tone === "neutral" ? 0.06 : 0.1)};
      color: ${c};
      border-color: ${alpha(c, 0.25)};
      ${$pulse &&
      css`
        animation: ${pulse} 1.2s ease infinite;
      `}
    `;
  }}
`;

// ─── EnvelopeTypeBadge ────────────────────────────────────────────────────────
// From SignResultPanel — generalized tone mapping instead of hardcoded switch.

export const EnvelopeTypeBadge = styled.div<{
  $type: "single" | "multi" | "open" | "sealed";
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 7px;
  border-radius: 100px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  letter-spacing: 0.05em;
  text-transform: uppercase;
  border: 1px solid transparent;
  ${({ $type, theme }) => {
    const tone: StatusTone =
      $type === "sealed"
        ? "danger"
        : $type === "multi"
          ? "info"
          : $type === "open"
            ? "success"
            : "neutral";
    const c = toneColor(tone, theme);
    return css`
      background: ${alpha(c, tone === "neutral" ? 0.05 : 0.08)};
      color: ${c};
      border-color: ${alpha(c, tone === "neutral" ? 0.15 : 0.22)};
      opacity: ${tone === "neutral" ? 0.7 : 1};
    `;
  }}
`;

// ─── Top-of-card progress bar with shimmer ───────────────────────────────────
// Used by SignPanelAdvanced's Card headers and SignStep's wizard progress —
// was duplicated locally in the former before this hoist.

export const ProgressBarWrap = styled.div`
  height: 2px;
  width: 100%;
  background: ${({ theme }) => theme.colors.primarySoft};
  flex-shrink: 0;
  overflow: hidden;
  position: relative;
`;

export const ProgressBarFill = styled.div<{
  $progress: number;
  $color?: string;
}>`
  height: 100%;
  border-radius: 1px;
  width: ${({ $progress }) => $progress}%;
  background: ${({ $color, theme }) =>
    $color ??
    `linear-gradient(90deg, ${theme.colors.success}, ${alpha(theme.colors.success, 0.55)})`};
  transition: width 400ms cubic-bezier(0.4, 0, 0.2, 1);
  ${({ $progress }) =>
    $progress > 0 &&
    $progress < 100 &&
    css`
      &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(
          90deg,
          transparent 0%,
          rgba(255, 255, 255, 0.18) 50%,
          transparent 100%
        );
        animation: ${shimmer} 1.4s ease infinite;
      }
    `}
`;
// ─── Metadata grid (InfoRow / InfoLabel / InfoValue) ─────────────────────────
// Verbatim duplicate in SignResultPanel.tsx + UserTSACredits.tsx, now canonical.
// $accent (5 string union) → $tone (StatusTone), to share vocabulary with
// StatusBadge instead of maintaining a second color-name enum.
// $numeric is new: routes byte sizes / counts / timestamps to fonts.numbers
// instead of defaulting everything non-mono to a generic sans weight.

export const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 11px;
  width: 100%;

  border-bottom: 1px solid ${({ theme }) => theme.colors.secondaryBackground};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex-direction: column;
    gap: 0;
    align-items: flex-start;
  }
`;

export const InfoLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.55;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  flex: 0 0 9rem;
  display: flex;
  align-items: center;
  gap: 4px;
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    flex: unset;
  }
`;

export const InfoValue = styled.div<{
  $mono?: boolean;
  $numeric?: boolean;
  $tone?: StatusTone;
  $wrap?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 6px;

  min-width: 0;
  flex: 1;

  font-family: ${({ $mono, $numeric, theme }) =>
    $mono
      ? theme.typography.fonts.mono
      : $numeric
        ? theme.typography.fonts.numbers
        : theme.typography.fonts.semibold};

  font-size: ${({ $mono, theme }) =>
    $mono ? theme.typography.sizes.helper : theme.typography.sizes.body};

  text-align: left;

  color: ${({ $tone, theme }) =>
    $tone ? toneColor($tone, theme) : theme.colors.textPrimary};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    max-width: 340px;
  }

  ${({ $wrap }) =>
    $wrap
      ? css`
          span {
            min-width: 0;
            overflow-wrap: anywhere;
            white-space: normal;
          }
        `
      : css`
          span {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        `}
`;

export const ActionBtn = styled.button<{
  $variant?: "success" | "brand" | "danger" | "dangerSoft" | "ghost";
  $pulse?: boolean;
}>`
  flex: 1;
  min-width: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 14px;
  border-radius: 9px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  cursor: pointer;
  border: 1px solid;
  transition: all 120ms ease;
  ${({ $variant, $pulse, theme }) => {
    if ($variant === "success")
      return css`
        background: ${alpha(theme.colors.success, 0.1)};
        color: ${theme.colors.success};
        border-color: ${alpha(theme.colors.success, 0.25)};
        &:hover {
          background: ${theme.colors.success};
          color: ${theme.colors.textPrimary};
        }
      `;
    if ($variant === "brand")
      return css`
        background: ${alpha(theme.colors.primary, 0.1)};
        color: ${theme.colors.primary};
        border-color: ${alpha(theme.colors.primary, 0.25)};
        &:hover {
          background: ${theme.colors.primary};
          color: ${theme.colors.textPrimary};
        }
      `;
    if ($variant === "danger")
      return css`
        background: ${alpha(theme.colors.error, $pulse ? 0.15 : 0.08)};
        color: ${theme.colors.error};
        border-color: ${alpha(theme.colors.error, 0.25)};
        ${$pulse &&
        css`
          animation: ${pulse} 1.2s ease infinite;
        `}
        &:hover {
          background: ${theme.colors.error};
          color: ${theme.colors.textPrimary};
        }
      `;
    if ($variant === "dangerSoft")
      return css`
        background: ${alpha(theme.colors.error, 0.07)};
        color: ${theme.colors.error};
        border-color: ${alpha(theme.colors.error, 0.2)};
        &:hover {
          background: ${theme.colors.error};
          color: ${theme.colors.textPrimary};
        }
      `;
    return css`
      background: transparent;
      color: ${theme.colors.textSecondary};
      border-color: ${theme.colors.secondaryBackground};
      &:hover {
        background: ${theme.colors.secondaryBackground};
        color: ${theme.colors.textPrimary};
      }
    `;
  }}
  &:active {
    opacity: 0.75;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    pointer-events: none;
  }
`;

// ─── Signatory progress block ─────────────────────────────────────────────────
// Duplicated identically in old SignPanel.tsx + SignResultPanel.tsx.
// pre-existing terminology mismatch in the codebase, not something to fix here.

export const SignatoryRow = styled.div<{ $hasSigned: boolean }>`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 8px;
  border-radius: 6px;
  background: ${({ $hasSigned, theme }) =>
    alpha($hasSigned ? theme.colors.success : theme.colors.warning, 0.06)};
  border: 1px solid
    ${({ $hasSigned, theme }) =>
    alpha($hasSigned ? theme.colors.success : theme.colors.warning, 0.15)};
`;

export const SignatoryDot = styled.div<{ $hasSigned: boolean }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${({ $hasSigned, theme }) =>
    $hasSigned ? theme.colors.success : theme.colors.warning};
`;

export const SignatoryName = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SignatoryStatus = styled.span<{ $hasSigned: boolean }>`
  font-family: ${({ theme }) => theme.typography.fonts.numbers};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  font-weight: 600;
  color: ${({ $hasSigned, theme }) =>
    $hasSigned ? theme.colors.success : theme.colors.warning};
  flex-shrink: 0;
`;

export const IssuerBadge = styled.span`
  padding: 1px 5px;
  border-radius: 4px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  letter-spacing: 0.04em;
  text-transform: uppercase;
  background: ${({ theme }) => alpha(theme.colors.info, 0.1)};
  color: ${({ theme }) => theme.colors.textSecondary};
  border: 1px solid ${({ theme }) => alpha(theme.colors.info, 0.2)};
  flex-shrink: 0;
`;

export const ProgressCount = styled.div`
  font-family: ${({ theme }) => theme.typography.fonts.numbers};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.65;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const ProgressBar = styled.div<{ $pct: number }>`
  flex: 1;
  height: 3px;
  border-radius: 2px;
  background: ${({ theme }) => theme.colors.secondaryBackground};
  overflow: hidden;
  position: relative;
  &::after {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ theme }) => theme.colors.success};
    border-radius: 2px;
    transition: width 400ms ease;
  }
`;

// ─── Copy-to-clipboard icon button ───────────────────────────────────────────
// Small icon-only affordance for table-row values (fingerprint, hashes).
// Distinct from the bigger labeled "Copy" button pattern (e.g. detached
// signature blob) — that one stays local where it already lives.

export const CopyIconBtn = styled.button<{ $copied?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ $copied, theme }) =>
    $copied ? theme.colors.success : theme.colors.textSecondary};
  opacity: ${({ $copied }) => ($copied ? 1 : 0.5)};
  cursor: pointer;
  border-radius: 4px;
  transition: all 120ms ease;
  &:hover {
    opacity: 1;
    background: ${({ theme }) => theme.colors.primaryBackground};
  }
`;

export const CopyField: React.FC<{ value: string; label?: string }> = ({
  value,
  label,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        toast.success("Copied to clipboard", {
          id: `toast-copy-${label}`,
          description: label?.toUpperCase(),
        });
        setTimeout(() => setCopied(false), 1500);
      } catch {
        /* ignore */
      }
    },
    [label, value],
  );

  return (
    <CopyIconBtn
      type="button"
      $copied={copied}
      onClick={handleCopy}
      aria-label={label ? `Copy ${label}` : "Copy"}
      title={copied ? "Copied!" : label ? `Copy ${label}` : "Copy"}
    >
      {copied ? <CheckIcon size={12} weight="bold" /> : <CopyIcon size={12} />}
    </CopyIconBtn>
  );
};

export const OpenURLBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.success};

  cursor: pointer;
  border-radius: 4px;
  transition: all 120ms ease;
  &:hover {
    opacity: 1;
    background: ${({ theme }) => alpha(theme.colors.success, 0.1)};
  }
`;

export const OpenURLButton: React.FC<{ url: string; label?: string }> = ({
  url,
  label,
}) => {
  const handleOpenURL = useCallback(() => {
    openURLNewTab(url);
  }, [url]);

  return (
    <OpenURLBtn
      type="button"
      onClick={handleOpenURL}
      aria-label={label ? `Open ${label}` : "Open URL"}
      title={label ? `Open ${label}` : "Open URL"}
    >
      <ArrowSquareOutIcon size={12} />
    </OpenURLBtn>
  );
};

// ─── Responsive 2-up card row ─────────────────────────────────────────────────
// auto-fit + 1fr means: 2 children → side by side; 1 child → fills full width;
// narrow viewport → wraps to stacked automatically, no extra media query needed.

export const CardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

// ─── Expected/actual signers section ─────────────────────────────────────────
// Used in both SignPanel.tsx's header (expected-signers picker) and
// SignPanelAdvanced.tsx's body (Signatories progress block).

export const SignersSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 11px;
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: 8px;
  animation: ${fadeUp} 180ms ease both;
`;

export const SignersSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const SignersSectionLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.helper};
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.5;
`;

export const ClearBtn = styled.button`
  display: flex;
  flex-direction: row;
  align-content: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  padding: 3px 9px;
  border-radius: 5px;
  border: 1px solid ${({ theme }) => theme.colors.primaryBackground};
  background: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
  &:hover {
    background: ${({ theme }) => alpha(theme.colors.error, 0.07)};
    color: ${({ theme }) => theme.colors.error};
    border-color: ${({ theme }) => alpha(theme.colors.error, 0.3)};
  }
`;

export const ViewModeSwitcher = styled.div`
  display: flex;
  align-items: center;
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: 9px;
  padding: 3px;
  gap: 2px;
  flex-shrink: 0;
`;

export const ViewModePill = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 7px;
  font-family: ${({ theme }) => theme.typography.fonts.medium};
  font-size: 11px;
  letter-spacing: -0.01em;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.primary};
          color: ${theme.colors.textPrimary};
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};

          &:hover {
            color: ${theme.colors.textPrimary};
          }
        `}

  @media (max-width: 800px) {
    padding: 6px;
    gap: 0;

    span {
      display: none;
    }
  }
`;

export const SpinIcon = styled.span`
  display: inline-flex;
  animation: ${spin} 0.9s linear infinite;
`;

export const Card = styled.div`
  background: ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: 12px;
  overflow: hidden;
`;

export const FieldRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primaryBackground};
  &:last-child {
    border-bottom: none;
  }
`;

export const FieldIcon = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.45;
  padding-top: 1px;
`;

export const FieldContent = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const FieldLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.4;
`;

export const FieldValue = styled.span`
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};
  word-break: break-all;
  line-height: 1.4;
`;

export const FieldMono = styled(FieldValue)`
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  font-size: 10px;
  opacity: 0.7;
`;

export const FieldCopyBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.3;
  flex-shrink: 0;
  &:hover {
    opacity: 0.8;
  }
`;

export const EmptyField = styled.span`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.25;
  font-style: italic;
`;

export const SectionWrap = styled.div`
  margin-bottom: 12px;
  height: auto;
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 0 6px;
`;

export const SectionTitle = styled.h4`
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.45;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 5px;
`;

export const SectionAction = styled.button`
  font-size: 10px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary || "#E05C1A"};
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 0;
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.8;
  &:hover {
    opacity: 1;
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const IconBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: all 0.15s;
  &:hover {
    background: ${({ theme }) => theme.colors.secondaryBackground};
  }
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const ImportModeToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
`;

export const ModeToggleButton = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.secondaryBackground};
  background: ${({ theme, $active }) =>
    $active ? `${theme.colors.primary}18` : theme.colors.secondaryBackground};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  letter-spacing: 0.02em;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// ─── Constants ─────────────────────────────────────────────────────────────────

export const REVOKE_LOCKOUT_MS = 30 * 24 * 60 * 60 * 1000;

export const ACTIVE_SESSION_STATUSES = new Set([
  "Not Started",
  "In Progress",
  "In Review",
  "Resubmitted",
]);

export const STAGE_LABELS: Record<string, string> = {
  id_verification: "ID Verification",
  liveness: "Liveness Check",
  face_match: "Face Match",
  ip_analysis: "IP Analysis",
};

export const ALL_STAGES = [
  "id_verification",
  "liveness",
  "face_match",
  "ip_analysis",
];

export const HeroWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
  padding: 60px 24px 20px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 40px 20px 12px;
  }
`;

export const Eyebrow = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin: 0;
  animation: ${fadeUp} 0.5s ease;
`;

export const HeroTitle = styled.h2`
  font-family: ${({ theme }) => theme.typography.fonts.bold};
  font-weight: ${({ theme }) => theme.typography.weights.header};
  font-size: clamp(
    1.9rem,
    4vw,
    ${({ theme }) => theme.typography.sizes.header}
  );
  line-height: ${({ theme }) => theme.typography.lineHeights.heading};
  letter-spacing: ${({ theme }) => theme.typography.letterSpacing.heading};
  background: ${({ theme }) => theme.gradients.strong};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  max-width: 30ch;
  animation: ${fadeUp} 0.6s ease 0.05s backwards;
`;

export const HeroDescription = styled.p`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.subject};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: ${({ theme }) => theme.typography.lineHeights.body};
  max-width: 52ch;
  margin: 0;
  animation: ${fadeUp} 0.6s ease 0.1s backwards;
`;

export const HeroActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 6px;
  animation: ${fadeUp} 0.6s ease 0.15s backwards;

  & > button {
    flex: 0 0 auto;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    width: 100%;
    & > button {
      flex: 1 1 auto;
    }
  }
`;

// add to SharedComponents.tsx

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.small};

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

export const FormLabel = styled.label`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.5;
  display: flex;
  align-items: center;
  gap: 4px;
`;

export const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors.error};
`;

const fieldBaseStyles = css`
  width: 100%;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: 1px solid ${({ theme }) => theme.colors.primaryBackground};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  color: ${({ theme }) => theme.colors.textPrimary};
  box-sizing: border-box;
  transition: border-color 0.15s;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &::placeholder {
    color: ${({ theme }) => theme.colors.textSecondary};
    opacity: 0.3;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FieldInput = styled.input`
  ${fieldBaseStyles}
`;

export const FieldTextarea = styled.textarea`
  ${fieldBaseStyles}
  resize: vertical;
  min-height: 100px;
  line-height: 1.55;
`;

export const FieldSelect = styled.select`
  ${fieldBaseStyles}
  cursor: pointer;
`;

export const ChipGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

export const Chip = styled.button<{ $selected: boolean }>`
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.borders.radius.rounded};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid
    ${({ $selected, theme }) =>
    $selected
      ? alpha(theme.colors.primary, 0.4)
      : theme.colors.primaryBackground};
  background: ${({ $selected, theme }) =>
    $selected ? alpha(theme.colors.primary, 0.1) : "transparent"};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.primary : theme.colors.textSecondary};

  &:hover {
    border-color: ${({ theme }) => alpha(theme.colors.primary, 0.4)};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

// ─── ServiceIconBadge ─────────────────────────────────────────────────────

export const ServiceIconBadge = styled.div<{
  $tone?: StatusTone;
  $size?: number;
}>`
  width: ${({ $size }) => $size ?? 52}px;
  height: ${({ $size }) => $size ?? 52}px;
  border-radius: ${({ theme }) => theme.borders.radius.large};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  ${({ $tone, theme }) => {
    const c = $tone ? toneColor($tone, theme) : theme.colors.primary;
    return css`
      background: ${alpha(c, 0.1)};
      border: 1px solid ${alpha(c, 0.22)};
      color: ${c};
    `;
  }}
`;

// ─── EndpointPill ───────────────────────────────────────────────────────────
// Mono method+path pill, tone-mapped by HTTP method. Links out to API docs.

const ENDPOINT_METHOD_TONE: Record<string, StatusTone> = {
  GET: "info",
  POST: "success",
  DELETE: "danger",
  PUT: "warning",
  PATCH: "warning",
};

export const EndpointPill = styled.a<{ $method: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 9px;
  border-radius: ${({ theme }) => theme.borders.radius.small};
  font-family: ${({ theme }) => theme.typography.fonts.mono};
  font-size: 10px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 120ms ease;
  ${({ $method, theme }) => {
    const c = toneColor(ENDPOINT_METHOD_TONE[$method] ?? "neutral", theme);
    return css`
      border: 1px solid ${alpha(c, 0.25)};
      background: ${alpha(c, 0.06)};
      color: ${c};
      &:hover {
        background: ${alpha(c, 0.12)};
      }
      span {
        font-weight: 700;
        opacity: 0.75;
      }
    `;
  }}
`;

// ─── UseCaseCard ────────────────────────────────────────────────────────────
// Sits inside CardRow. Industry tag + short real-world scenario.

export const UseCaseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px 15px;
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  flex: 1;
  height: 100%;
  display: flex;
`;

export const UseCaseIndustry = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: 10px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  opacity: 0.85;
`;

export const UseCaseTitle = styled.h5`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: 0;
  line-height: 1.4;
`;

export const UseCaseBody = styled.p`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.hint};
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.75;
  line-height: 1.55;
  margin: 0;
`;

// ─── ResourceLinkRow ────────────────────────────────────────────────────────
// External-resource row (GitHub, npm, docs, articles). Placeholder-safe:
// pass href="#" until real links exist.

export const ResourceLinkRow = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 13px;
  border-radius: ${({ theme }) => theme.borders.radius.medium};
  background: ${({ theme }) => theme.colors.primaryBackground};
  border: 1px solid ${({ theme }) => theme.colors.secondaryBackground};
  text-decoration: none;
  transition: all 120ms ease;
  &:hover {
    border-color: ${({ theme }) => alpha(theme.colors.primary, 0.3)};
    background: ${({ theme }) => alpha(theme.colors.primary, 0.05)};
  }
`;

export const ResourceLinkText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
`;

export const ResourceLinkLabel = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.semibold};
  font-size: ${({ theme }) => theme.typography.sizes.body};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

export const ResourceLinkSub = styled.span`
  font-family: ${({ theme }) => theme.typography.fonts.regular};
  font-size: ${({ theme }) => theme.typography.sizes.tab};
  color: ${({ theme }) => theme.colors.textSecondary};
  opacity: 0.55;
`;


export const StickyFooter = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    position: sticky;
    bottom: 0;
    z-index: 10;
    margin-top: auto;

    /* opaque so content doesn't show through while scrolling */
    background: ${({ theme }) => alpha(theme.colors.primaryBackground, 0.9)};
    backdrop-filter: blur(6px);
  
    /* breathing room + iPhone home-indicator safe area */
    padding: 12px 0 calc(12px + env(safe-area-inset-bottom, 0px));
    padding-bottom: 3em;
    padding-top: 1em;
  }
`;